import * as React from 'react';
import { useCallback } from 'react';
import cn from 'classnames';

import { SubsidyOption } from '../../../types';
import SubsidiesSelector from '../../../../Subsidies/components/SubsidiesSelector/SubsidiesSelector';

import styles from './PassengerSubsidySelect.css';

export interface PassengerSubsidySelectProps {
	name: string;
	options: SubsidyOption[];
	value?: string | string[];
	onChange: (value: string | string[], event: React.ChangeEvent) => void;
	onBlur?: () => void;
	disabled?: boolean;
	error?: string;
	className?: string;
	multiple?: boolean;
}

const PassengerSubsidySelect: React.FC<PassengerSubsidySelectProps> = props => {
	const { options, value, onChange, onBlur, disabled, error, className, multiple = false } = props;

	const handleOptionClick = useCallback(
		(optionKey: string) => (event: React.ChangeEvent) => {
			// Приводим к HTMLInputElement для доступа к checked
			const inputEvent = event as React.ChangeEvent<HTMLInputElement>;
			const isChecked = inputEvent.target.checked;

			if (multiple) {
				// Multi-select режим
				const currentValue = Array.isArray(value) ? value : value ? [value] : [];
				const newValue = isChecked ? [...currentValue, optionKey] : currentValue.filter(id => id !== optionKey);

				// Создаем синтетическое событие с правильным target.value
				const syntheticEvent = {
					...event,
					target: {
						...event.target,
						value: newValue
					}
				} as React.ChangeEvent;

				onChange(newValue, syntheticEvent);
			} else {
				// Single-select режим
				const newValue = isChecked ? optionKey : '';

				// Создаем синтетическое событие с правильным target.value
				const syntheticEvent = {
					...event,
					target: {
						...event.target,
						value: newValue
					}
				} as React.ChangeEvent;

				onChange(newValue, syntheticEvent);
			}
		},
		[value, onChange, multiple]
	);

	const isSelected = useCallback(
		(optionKey: string): boolean => {
			if (multiple) {
				const currentValue = Array.isArray(value) ? value : value ? [value] : [];

				return currentValue.includes(optionKey);
			} else {
				const currentValue = Array.isArray(value) ? value[0] : value;

				return currentValue === optionKey;
			}
		},
		[value, multiple]
	);

	return (
		<div className={cn(styles.container, className)} onBlur={onBlur}>
			<div className={styles.optionsList}>
				{options.map(option => (
					<SubsidiesSelector
						key={option.key}
						id={option.key}
						type={option.type}
						isSelected={isSelected(option.key)}
						onChange={handleOptionClick(option.key)}
						disabled={disabled || option.disabled}
						className={styles.option}
					/>
				))}
			</div>
			{error && <div className={styles.error}>{error}</div>}
		</div>
	);
};

export default PassengerSubsidySelect;
